/**
 * Personal access tokens: list, create with a one-time reveal, revoke.
 *
 * **The reveal dialog is the highest-stakes moment in this app.** The value exists in exactly one
 * HTTP response and nowhere else — the service stores a SHA-256 digest, so there is no code path
 * that could return it again, not for support, not for the owner. A dialog that closes while
 * somebody is still reading destroys a credential. So:
 *
 *   - Escape does not close it and neither does a backdrop click (`dismissible={false}`).
 *   - The close button is **disabled until the value has actually been copied**, and the copy
 *     button reports its own failure rather than pretending. `navigator.clipboard` is unavailable
 *     on an insecure origin, and a silent no-op there would be the worst possible outcome.
 *   - There is a visible way out for somebody who copied it by hand — an explicit
 *     "I saved it another way" — because a dialog with no exit is a dialog people close by
 *     reloading the page, which is the same data loss with extra steps.
 *
 * The other thing this screen says that the design does not: revocation is not instant. A revoked
 * token keeps working for up to the principal cache's TTL, per running instance. Somebody revoking
 * a token from a lost laptop has to know that.
 */

import { useCallback, useState } from "react";

import { CopyButton } from "../components/copy-button";
import { Dialog } from "../components/dialog";
import { EmptyState, ErrorNotice, Loading } from "../components/feedback";
import { TokenStateChip } from "../components/state-chip";
import { useToast } from "../components/toast";
import { ApiError } from "../lib/api/errors";
import * as tokensApi from "../lib/api/tokens-api";
import { tokenState, type CreatedToken, type PersonalToken } from "../lib/api/types";
import { absoluteDate, relativeTime } from "../lib/ui/format";
import { SLOW_POLL_MS, usePollTick } from "../lib/ui/use-poll";
import { useResource } from "../lib/ui/use-resource";

export function TokensScreen() {
  const toast = useToast();
  const load = useCallback((signal: AbortSignal) => tokensApi.listTokens(signal), []);
  // Refetched on a timer and whenever the tab is looked at again. `last_used_at` moves on its own
  // — an assistant connects while this page is open — and a row that goes on saying "Created, but
  // no assistant has connected with it" after one has is the screen telling somebody their install
  // failed when it worked.
  const tokens = useResource(load, [usePollTick(SLOW_POLL_MS)]);

  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<CreatedToken | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<PersonalToken | null>(null);

  async function create() {
    setCreating(true);
    setCreateError(null);
    try {
      const created = await tokensApi.createToken(name.trim());
      setNaming(false);
      setName("");
      // Straight into the reveal. There is no gap in which this value could be lost to a reload.
      setRevealed(created);
      tokens.reload();
    } catch (cause) {
      setCreateError(
        cause instanceof ApiError ? cause.message : "Could not create that token. Try again.",
      );
    } finally {
      setCreating(false);
    }
  }

  async function revoke() {
    const token = pendingRevoke;
    if (token === null) return;
    setPendingRevoke(null);
    try {
      await tokensApi.revokeToken(token.id);
      toast("Token revoked. Assistants using it stop working within a minute.");
      tokens.reload();
    } catch {
      toast("Could not revoke that token. Try again.");
    }
  }

  const rows = tokens.data ?? [];

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">Tokens</h1>
        <button
          className="button-primary page-head__action"
          type="button"
          onClick={() => {
            setCreateError(null);
            setNaming(true);
          }}
        >
          Create a token
        </button>
      </div>
      <p className="page-lede">
        One per machine is plenty. A token&rsquo;s value is shown once, when it is created.
      </p>

      {tokens.loading && <Loading what="your tokens" />}
      {tokens.error !== null && <ErrorNotice error={tokens.error} onRetry={tokens.reload} />}

      {tokens.data !== null && rows.length === 0 && (
        <EmptyState
          headline="No tokens yet."
          actions={
            <button
              className="button-primary"
              type="button"
              onClick={() => {
                setNaming(true);
              }}
            >
              Create a token
            </button>
          }
        >
          <p className="empty__body">
            A token is how an assistant proves it is yours. Create one, then paste the command from
            Install into your client.
          </p>
        </EmptyState>
      )}

      {rows.length > 0 && (
        <ul className="token-list">
          {rows.map((token) => {
            const state = tokenState(token);
            return (
              <li className="token-row" key={token.id} data-revoked={state === "revoked"}>
                <div className="token-row__main">
                  <span className="token-row__name">{token.name}</span>
                  <span className="token-row__hint">
                    {state === "revoked"
                      ? `Revoked ${relativeTime(token.revoked_at)} — assistants that used it can no longer reach Athena.`
                      : state === "never-used"
                        ? "Created, but no assistant has connected with it."
                        : `Last used ${relativeTime(token.last_used_at)}.`}
                  </span>
                  <span className="token-row__created">
                    Created {absoluteDate(token.created_at)}
                  </span>
                </div>
                <div className="token-row__side">
                  <TokenStateChip state={state} />
                  {state !== "revoked" && (
                    <button
                      className="button-secondary"
                      type="button"
                      onClick={() => {
                        setPendingRevoke(token);
                      }}
                    >
                      Revoke
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <p className="page-footnote">
        A token that was never used usually means the install command was never run. Revoking is not
        instant: a revoked token can keep working for up to a minute per running server instance
        while the credential cache expires.
      </p>

      {naming && (
        <Dialog
          title="Create a token"
          onClose={() => {
            setNaming(false);
          }}
          footer={
            <>
              <button
                className="button-quiet"
                type="button"
                onClick={() => {
                  setNaming(false);
                }}
              >
                Cancel
              </button>
              <button
                className="button-primary"
                type="button"
                disabled={name.trim() === "" || creating}
                onClick={() => void create()}
              >
                {creating ? "Creating…" : "Create token"}
              </button>
            </>
          }
        >
          <p className="dialog__body">Name it after the machine it is for.</p>
          <input
            className="field-input"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
            placeholder="e.g. work laptop"
            aria-label="Token name"
          />
          {createError !== null && (
            <p className="dialog__error" role="alert">
              {createError}
            </p>
          )}
        </Dialog>
      )}

      {revealed !== null && (
        <RevealDialog
          token={revealed}
          onDone={() => {
            setRevealed(null);
          }}
        />
      )}

      {pendingRevoke !== null && (
        <Dialog
          title={`Revoke “${pendingRevoke.name}”?`}
          onClose={() => {
            setPendingRevoke(null);
          }}
          footer={
            <>
              <button
                className="button-quiet"
                type="button"
                onClick={() => {
                  setPendingRevoke(null);
                }}
              >
                Cancel
              </button>
              <button className="button-secondary" type="button" onClick={() => void revoke()}>
                Revoke token
              </button>
            </>
          }
        >
          <p className="dialog__body">
            Any assistant connected with this token stops working — mid-session, without warning,
            within about a minute. If that machine still needs Athena, create its new token first.
          </p>
        </Dialog>
      )}
    </>
  );
}

/**
 * The one-time reveal.
 *
 * `dismissible={false}` is the whole point: Escape and backdrop clicks are the two ways a person
 * closes a dialog without reading it, and this is the one dialog where doing so costs them
 * something they cannot get back.
 */
function RevealDialog({ token, onDone }: { token: CreatedToken; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  const [acknowledgedByHand, setAcknowledgedByHand] = useState(false);
  const canClose = copied || acknowledgedByHand;

  return (
    <Dialog
      eyebrow="Shown once"
      title="Copy this now. You will not be able to see it again."
      dismissible={false}
      onClose={onDone}
      footer={
        <>
          {!canClose && (
            <span className="dialog__hint">
              Copy it before closing.{" "}
              <button
                className="link-button"
                type="button"
                onClick={() => {
                  setAcknowledgedByHand(true);
                }}
              >
                I saved it another way
              </button>
            </span>
          )}
          <button
            className="button-primary"
            type="button"
            disabled={!canClose}
            onClick={onDone}
          >
            I have copied it — done
          </button>
        </>
      }
    >
      <p className="dialog__body">
        Athena keeps only a fingerprint of this token. If you lose it, the only fix is to revoke it
        and create another — there is no screen, and no support request, that can show it again.
      </p>
      <div className="reveal">
        <code className="reveal__value">{token.value}</code>
        <CopyButton
          value={token.value}
          className="reveal__copy"
          onCopied={() => {
            setCopied(true);
          }}
        />
      </div>
      <p className="dialog__body">
        Paste it into the install command on the <strong>Install</strong> screen, replacing the
        placeholder.
      </p>
    </Dialog>
  );
}
